import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useIsAdmin } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { SimulationsSection } from "@/components/practice/SimulationsSection";
import { QuizHistory } from "@/components/practice/QuizHistory";
import { TrialBanner } from "@/components/practice/TrialBanner";
import { TopicRankings } from "@/components/practice/TopicRankings";
import { CertificatesTab } from "@/components/practice/CertificatesTab";
import { CandidateHeader } from "@/components/candidate/Header";
import { CandidateSidebar } from "@/components/candidate/Sidebar";
import { WelcomeModal, useWelcomeModal } from "@/components/academy/WelcomeModal";
import { TrialInstructionsModal } from "@/components/academy/TrialInstructionsModal";
import { DiagnosticModal } from "@/components/academy/DiagnosticModal";
import { supabase as supabaseClient } from "@/integrations/supabase/client";
import { PaywallDialog } from "@/components/practice/PaywallDialog";
import { PremiumLockedContent } from "@/components/practice/PremiumLockedContent";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { AcademyHome } from "@/components/academy/AcademyHome";
import { StudyTrailView } from "@/components/practice/StudyTrailView";
import { ResumeAnalyzerView } from "@/components/resume-analyzer/ResumeAnalyzerView";
import { ChallengesView } from "@/components/challenges/ChallengesView";
import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuthUser } from "@/hooks/useAuth";
import {
  CourseSchema,
  LearningResourceSchema,
  EducationalOrganizationSchema,
} from "@/components/seo";

type AcademyView = "home" | "trail" | "certificates" | "history" | "ranking" | "simulations" | "challenges" | "resume-analyzer";
type Category = "power_bi" | "sql" | "python";

const categoryTitles: Record<Category, string> = {
  power_bi: "Power BI",
  sql: "SQL",
  python: "Python",
};

export default function Practice() {
  usePageMeta({
    title: "Academy - Data Hire Club",
    description: "Aprenda com vídeo-aulas e pratique com trilhas técnicas sobre Power BI, SQL e Python",
    noindex: true,
  });

  const { isAdmin } = useIsAdmin();
  const { showWelcome, setShowWelcome } = useWelcomeModal();
  const { isEnabled: academyEnabled, isLoading: flagLoading } = useFeatureFlag("academy_enabled");
  const { isTrialActive, isPremium, daysRemaining, planType, isLoading: subscriptionLoading } = useSubscriptionStatus();
  const { data: user } = useAuthUser();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentView, setCurrentView] = useState<AcademyView>("home");
  const [selectedCategory, setSelectedCategory] = useState<Category>("power_bi");
  const [showPaywall, setShowPaywall] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [diagnosticCategory, setDiagnosticCategory] = useState<Category | null>(null);

  // Handle Stripe checkout return
  useEffect(() => {
    const subStatus = searchParams.get("subscription");
    if (!subStatus) return;

    if (subStatus === "success") {
      toast({
        title: "🎉 Bem-vindo ao PRO!",
        description: "Sincronizando sua assinatura...",
      });
      // Sync subscription with Stripe
      supabase.functions.invoke("check-academy-subscription").then(({ error }) => {
        if (error) {
          console.error("Sync error:", error);
        }
        if (user?.id) {
          queryClient.invalidateQueries({ queryKey: ["subscription-status", user.id] });
        }
      });
    } else if (subStatus === "cancelled") {
      toast({
        title: "Checkout cancelado",
        description: "Você pode assinar a qualquer momento.",
      });
    }

    // Clean URL
    searchParams.delete("subscription");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, user?.id, queryClient]);

  const hasBasicAccess = isTrialActive || isPremium || isAdmin;
  const hasPremiumAccess = isPremium || isAdmin;

  // Auto-start trial: se não é admin, academy está habilitado, e usuário está free (nunca teve trial),
  // dispara o trial + popup na primeira visita ao Academy.
  useEffect(() => {
    if (subscriptionLoading || flagLoading) return;
    if (isAdmin) return;
    if (!academyEnabled) return;
    if (!user?.id) return;
    // Só dispara se está genuinamente free (sem trial e sem premium)
    const shouldAutoStart =
      !isPremium && !isTrialActive && !localStorage.getItem("academy_trial_auto_attempted");

    if (!shouldAutoStart) return;
    localStorage.setItem("academy_trial_auto_attempted", "true");

    supabase.functions.invoke("start-academy-trial").then(({ data, error }) => {
      if (error) {
        console.error("start-academy-trial error:", error);
        return;
      }
      if (data?.created) {
        queryClient.invalidateQueries({ queryKey: ["subscription-status", user.id] });
        // abre o popup só se ainda não foi visto
        if (!localStorage.getItem("academy_trial_instructions_seen")) {
          setShowTrialModal(true);
        }
      }
    });
  }, [subscriptionLoading, flagLoading, isAdmin, academyEnabled, user?.id, isPremium, isTrialActive, queryClient]);

  const handleStartTrialClick = async () => {
    if (!user?.id) return;
    const { data, error } = await supabase.functions.invoke("start-academy-trial");
    if (error) {
      toast({ title: "Erro ao iniciar trial", description: error.message, variant: "destructive" });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["subscription-status", user.id] });
    setShowTrialModal(true);
    if (data?.created) {
      toast({ title: "🎉 Trial iniciado!", description: "Você tem 7 dias para explorar." });
    }
  };

  if (subscriptionLoading || flagLoading) {
    return (
      <div className="min-h-screen bg-background">
        <CandidateHeader />
        <div className="flex">
          <div className="hidden md:block"><CandidateSidebar /></div>
          <main className="flex-1 py-8 px-4 md:px-8 md:ml-64">
            <div className="max-w-5xl mx-auto">
              <p className="text-muted-foreground">Carregando...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Se Academy está desligado para candidatos e o usuário não é admin, redireciona
  if (!isAdmin && !academyEnabled) {
    return <Navigate to="/candidate/dashboard" replace />;
  }

  const handleNavigate = (view: AcademyView, category?: Category) => {
    if (category) setSelectedCategory(category);
    setCurrentView(view);
  };

  const handleBack = () => setCurrentView("home");

  // Verifica se aluno já fez diagnóstico da trilha. Se não, abre modal.
  const handleSelectTrail = async (category: Category) => {
    if (!user?.id) {
      setSelectedCategory(category);
      setCurrentView("trail");
      return;
    }
    const { data } = await supabaseClient
      .from("user_trail_diagnostic")
      .select("id")
      .eq("user_id", user.id)
      .eq("category", category)
      .maybeSingle();

    setSelectedCategory(category);
    if (!data) {
      setDiagnosticCategory(category);
    } else {
      setCurrentView("trail");
    }
  };

  const renderBackButton = (label: string) => (
    <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1.5 mb-4 -ml-2 text-muted-foreground hover:text-foreground">
      <ArrowLeft className="h-4 w-4" />
      {label}
    </Button>
  );

  const renderView = () => {
    switch (currentView) {
      case "home":
        return (
          <AcademyHome
            onNavigate={handleNavigate}
            onSelectTrail={handleSelectTrail}
            hasPremiumAccess={hasPremiumAccess}
            hasBasicAccess={hasBasicAccess}
            planType={planType}
            onUpgrade={() => setShowPaywall(true)}
            onStartTrial={handleStartTrialClick}
          />
        );

      case "trail":
        return (
          <StudyTrailView
            category={selectedCategory}
            categoryTitle={categoryTitles[selectedCategory]}
            onBack={handleBack}
            isPremium={hasPremiumAccess}
            isTrialActive={isTrialActive}
            onUpgrade={() => setShowPaywall(true)}
          />
        );

      case "certificates":
        return (
          <>
            {renderBackButton("Voltar ao Academy")}
            {hasPremiumAccess ? (
              <CertificatesTab />
            ) : (
              <PremiumLockedContent
                title="Certificados"
                description="Assine PRO para emitir certificados verificáveis."
                onUpgrade={() => setShowPaywall(true)}
              />
            )}
          </>
        );

      case "history":
        return (
          <>
            {renderBackButton("Voltar ao Academy")}
            {hasBasicAccess ? (
              <QuizHistory />
            ) : (
              <PremiumLockedContent
                title="Histórico"
                description="Assine para ver seu histórico."
                onUpgrade={() => setShowPaywall(true)}
              />
            )}
          </>
        );

      case "ranking":
        return (
          <>
            {renderBackButton("Voltar ao Academy")}
            <TopicRankings />
          </>
        );

      case "simulations":
        return (
          <>
            {renderBackButton("Voltar ao Academy")}
            {hasPremiumAccess ? (
              <SimulationsSection />
            ) : (
              <PremiumLockedContent
                title="Simulador de Entrevista"
                description="Recurso exclusivo para assinantes PRO. Pratique entrevistas técnicas em ambiente realista."
                features={[
                  "Entrevistas técnicas de SQL, Python e Power BI",
                  "Cases de negócio e análise",
                  "Feedback personalizado com IA",
                ]}
                onUpgrade={() => setShowPaywall(true)}
              />
            )}
          </>
        );

      case "challenges":
        return <ChallengesView />;

      case "resume-analyzer":
        return (
          <>
            {hasPremiumAccess ? (
              <ResumeAnalyzerView onBack={handleBack} />
            ) : (
              <>
                {renderBackButton("Voltar ao Academy")}
                <PremiumLockedContent
                  title="Analisador de Currículo"
                  description="Recurso exclusivo para assinantes PRO. Receba feedback com IA sobre seu CV."
                  features={[
                    "Aceita PDF, DOCX, TXT e mais",
                    "Compara com a vaga desejada",
                    "Sugestões objetivas de melhoria",
                  ]}
                  onUpgrade={() => setShowPaywall(true)}
                />
              </>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <CandidateLayout maxWidth="6xl">
      {/* SEO Schemas */}
      <EducationalOrganizationSchema
        name="Data Hire Club Academy"
        description="Plataforma de aprendizado para profissionais de dados com trilhas técnicas, vídeo-aulas, simulações de entrevista e certificados reconhecidos pelo mercado."
        url="https://datahireclub.com.br/candidate/practice"
        programs={[
          { name: "Fundamentos de SQL", description: "Aprenda SQL do básico ao avançado com quizzes práticos", url: "https://datahireclub.com.br/candidate/practice", educationalLevel: "Beginner", timeToComplete: "P2M" },
          { name: "Power BI para Analistas", description: "Domine Power BI com exercícios práticos e certificação", url: "https://datahireclub.com.br/candidate/practice", educationalLevel: "Intermediate", timeToComplete: "P3M" },
          { name: "Python para Dados", description: "Python aplicado à análise de dados com Pandas e NumPy", url: "https://datahireclub.com.br/candidate/practice", educationalLevel: "Intermediate", timeToComplete: "P4M" },
        ]}
      />
      <CourseSchema
        name="Programa Completo de Dados - Data Hire Club Academy"
        description="Trilha completa para carreira em dados: Power BI, SQL e Python com preparação para entrevistas técnicas."
        provider="Data Hire Club"
        url="https://datahireclub.com.br/candidate/practice"
        courseMode="online"
        educationalLevel="beginner"
        hasCertification={true}
        totalDuration="PT40H"
        numberOfModules={3}
        skills={["Power BI", "SQL", "Python", "Análise de Dados"]}
        modules={[
          { name: "Power BI Avançado", description: "DAX, modelagem e dashboards interativos", position: 1, duration: "PT15H" },
          { name: "Fundamentos de SQL", description: "SELECT, WHERE, JOIN, GROUP BY e funções de agregação", position: 2, duration: "PT10H" },
          { name: "Python para Análise", description: "Pandas, NumPy e visualização de dados", position: 3, duration: "PT15H" },
          { name: "Preparação para Entrevistas", description: "Simulações técnicas e cases de negócio", position: 4, duration: "PT5H" },
        ]}
      />
      <LearningResourceSchema
        name="Trilhas Técnicas de Dados"
        description="Pratique com questões técnicas sobre Power BI, SQL e Python. Inclui explicações detalhadas e acompanhamento de progresso."
        url="https://datahireclub.com.br/candidate/practice"
        learningResourceType="Quiz"
        educationalLevel="intermediate"
        educationalUse="assessment"
        timeRequired="PT15M"
        teaches={["Power BI", "SQL", "Python"]}
        assesses={["Consultas SQL", "Análise de dados", "Visualização", "Lógica de programação"]}
        numberOfQuestions={100}
        keywords={["quiz sql", "quiz python", "quiz power bi", "quiz dados", "prática dados"]}
      />

      <div className="space-y-6">
        <TrialBanner
          isTrialActive={isTrialActive}
          isPremium={isPremium}
          daysRemaining={daysRemaining}
          planType={planType}
          onUpgrade={() => setShowPaywall(true)}
          onStartTrial={handleStartTrialClick}
        />


        {renderView()}

        <WelcomeModal open={showWelcome} onOpenChange={setShowWelcome} />
        {diagnosticCategory && (
          <DiagnosticModal
            open={!!diagnosticCategory}
            onOpenChange={(o) => {
              if (!o) {
                const cat = diagnosticCategory;
                setDiagnosticCategory(null);
                if (cat) setCurrentView("trail");
              }
            }}
            category={diagnosticCategory}
          />
        )}
        <PaywallDialog open={showPaywall} onOpenChange={setShowPaywall} />
        <TrialInstructionsModal
          open={showTrialModal}
          onOpenChange={setShowTrialModal}
          daysRemaining={daysRemaining || 7}
          onSeePlans={() => setShowPaywall(true)}
        />
      </div>
    </CandidateLayout>
  );
}
